import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
	subagentFailureText,
	subagentResultNote,
	subagentStopReason,
	type ChatSubagentRun,
} from "@/lib/chat";
import { chatMarkdownComponents } from "./chat-markdown-components";
import { DetailBox } from "./ToolEntry";

const SUMMARY_CLASS =
	"select-text max-h-[300px] overflow-auto rounded-2 p-2 text-caption leading-body text-fg-1 bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-1)]";

/** 切り詰めの案内に添える、全文を開ける場所への誘導。 */
const FULL_TEXT_HINT = "全文はタスクタブで開けます。";

export function SubagentRunResult({
	run,
	fullTextHint = FULL_TEXT_HINT,
}: {
	run: ChatSubagentRun | undefined;
	/** タスクタブ自身のように、ここが全文の表示先である場合は空文字を渡して誘導を外す。 */
	fullTextHint?: string;
}) {
	if (!run) return null;
	// 要約より先に失敗を見る。要約が残る失敗もあり、逆順だと成功として描かれる。
	const failure = subagentFailureText(run);
	if (failure)
		return (
			<>
				<p
					data-testid="subagent-run-error"
					className="m-0 whitespace-pre-wrap wrap-anywhere text-caption text-danger"
				>
					{failure}
				</p>
				{run.errorTruncated && (
					<TruncationNote subject="失敗理由" hint={fullTextHint} testId="subagent-run-error" />
				)}
				{run.summary && (
					<SummaryBox summary={run.summary} truncated={run.summaryTruncated} hint={fullTextHint} />
				)}
			</>
		);
	// 停止は失敗ではないので danger にしない。理由が残っていれば、それが「停止済み」の説明になる。
	// 途中まで書いた要約が残る停止もあるため、理由の下に要約も出す。
	const stopReason = subagentStopReason(run);
	if (stopReason)
		return (
			<>
				<p
					data-testid="subagent-run-stop-reason"
					className="m-0 wrap-anywhere text-caption text-fg-2"
				>
					{stopReason}
				</p>
				{run.summary && (
					<SummaryBox summary={run.summary} truncated={run.summaryTruncated} hint={fullTextHint} />
				)}
			</>
		);
	if (run.summary)
		return (
			<SummaryBox summary={run.summary} truncated={run.summaryTruncated} hint={fullTextHint} />
		);
	const note = subagentResultNote(run.status);
	return note ? (
		<p data-testid="subagent-run-summary" className="m-0 text-caption text-fg-2">
			{note}
		</p>
	) : null;
}

/** 一覧が返す本文が先頭で切られていることの断り書き。 */
function TruncationNote({
	subject,
	hint,
	testId,
}: {
	subject: string;
	hint: string;
	testId: string;
}) {
	return (
		<p data-testid={`${testId}-truncated`} className="m-0 mt-1 text-caption text-fg-3">
			{subject}が長いため先頭のみ表示しています。{hint}
		</p>
	);
}

/**
 * 一覧が返す要約は先頭で切られている。切れているときはコピーの導線を出さない —
 * 「結果をコピー」で途中までしか取れないと、欠けたことに気づけないまま貼り付けてしまう。
 */
function SummaryBox({
	summary,
	truncated,
	hint,
}: {
	summary: string;
	truncated: boolean;
	hint: string;
}) {
	return (
		<DetailBox label="結果" copyLabel="結果をコピー" copyValue={truncated ? "" : summary}>
			<div data-testid="subagent-run-summary" className={SUMMARY_CLASS}>
				<ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
					{summary}
				</ReactMarkdown>
			</div>
			{truncated && <TruncationNote subject="結果" hint={hint} testId="subagent-run-summary" />}
		</DetailBox>
	);
}
